import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { cn } from "@/v2/lib/utils"
import { useIsMobile } from "@/v2/hooks/useIsMobile"

const ResponsiveDialog = DialogPrimitive.Root

const ResponsiveDialogTrigger = DialogPrimitive.Trigger

const ResponsiveDialogClose = DialogPrimitive.Close

const ResponsiveDialogPortal = DialogPrimitive.Portal

const ResponsiveDialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/60",
      className,
    )}
    {...props}
  />
))
ResponsiveDialogOverlay.displayName = "ResponsiveDialogOverlay"

interface ResponsiveDialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  title: string
  subtitle?: string
  footer?: React.ReactNode
}

const ResponsiveDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  ResponsiveDialogContentProps
>(({ className, title, subtitle, footer, children, ...props }, ref) => {
  const isMobile = useIsMobile()

  // Don't close the dialog on Escape if a dropdown or popover is open inside
  const handleEscapeKeyDown = React.useCallback((e: Event) => {
    const hasOpenDropdown = document.querySelector(
      '[role="listbox"], [data-radix-popper-content-wrapper]'
    )
    if (hasOpenDropdown) e.preventDefault()
  }, [])

  if (isMobile) {
    return (
      <ResponsiveDialogPortal>
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            "fixed inset-0 z-50 flex flex-col bg-background",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
            "duration-200",
            className,
          )}
          onEscapeKeyDown={handleEscapeKeyDown}
          {...props}
        >
          {/* Mobile header */}
          <div className="flex h-14 items-center justify-between border-b border-border px-4">
            <DialogPrimitive.Title className="text-base font-medium">
              {title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors">
              <X className="size-5" />
            </DialogPrimitive.Close>
          </div>

          {/* Mobile body */}
          <div className="flex-1 overflow-y-auto">
            {children}
          </div>

          {/* Mobile footer */}
          {footer && (
            <div className="border-t border-border p-4">
              {footer}
            </div>
          )}

          <DialogPrimitive.Description className="sr-only">
            {subtitle ?? title}
          </DialogPrimitive.Description>
        </DialogPrimitive.Content>
      </ResponsiveDialogPortal>
    )
  }

  return (
    <ResponsiveDialogPortal>
      <ResponsiveDialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-1/2 top-1/2 z-50 w-full max-w-lg max-h-[85vh] -translate-x-1/2 -translate-y-1/2 bg-background shadow-lg sm:rounded-lg",
          footer ? "flex flex-col" : "grid gap-4 overflow-y-auto p-6",
          className,
        )}
        onEscapeKeyDown={handleEscapeKeyDown}
        {...props}
      >
        {footer ? (
          <>
            {/* Desktop header */}
            <div className="flex flex-col space-y-1.5 sm:text-left p-6 pb-0">
              <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                {title}
              </DialogPrimitive.Title>
              {subtitle && (
                <DialogPrimitive.Description className="text-sm text-muted-foreground">
                  {subtitle}
                </DialogPrimitive.Description>
              )}
            </div>

            {/* Desktop body — scrollable */}
            <div className="flex-1 overflow-y-auto">
              {children}
            </div>

            {/* Desktop footer — pinned */}
            <div className="border-t border-border p-4">
              {footer}
            </div>
          </>
        ) : (
          <>
            {/* Desktop header */}
            <div className="flex flex-col space-y-1.5 sm:text-left">
              <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                {title}
              </DialogPrimitive.Title>
              {subtitle && (
                <DialogPrimitive.Description className="text-sm text-muted-foreground">
                  {subtitle}
                </DialogPrimitive.Description>
              )}
            </div>

            {/* Desktop body */}
            {children}
          </>
        )}

        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>

        {!subtitle && (
          <DialogPrimitive.Description className="sr-only">
            {title}
          </DialogPrimitive.Description>
        )}
      </DialogPrimitive.Content>
    </ResponsiveDialogPortal>
  )
})
ResponsiveDialogContent.displayName = "ResponsiveDialogContent"

export {
  ResponsiveDialog,
  ResponsiveDialogTrigger,
  ResponsiveDialogContent,
  ResponsiveDialogClose,
}
